import { ChatConversation, ChatMessage } from '../../features/chat/hooks/types';
import { ChatConversationRepository } from './ChatConversationRepository';
import { ChatMessageRepository } from './ChatMessageRepository';

/** Facade: delega para repositórios especializados (compatibilidade com hooks existentes). */
export class ChatRepository {
    private conversationRepo = new ChatConversationRepository();
    private messageRepo = new ChatMessageRepository();

    async upsertConversation(
        userId: string,
        connectionId: string,
        conversation: ChatConversation,
        organizationId?: string,
    ): Promise<string | null> {
        return this.conversationRepo.upsertConversation(userId, connectionId, conversation, organizationId);
    }

    async getConversationsPaginated(
        organizationId: string,
        cursor?: string,
        limit = 50,
        connectionId?: string,
    ): Promise<{ conversations: ChatConversation[]; hasMore: boolean; nextCursor?: string }> {
        return this.conversationRepo.getConversationsPaginated(organizationId, cursor, limit, connectionId);
    }

    async getConversationCounters(
        organizationId: string,
        userId?: string,
        connectionId?: string,
    ): Promise<{ unassigned: number; mine: number; all: number }> {
        return this.conversationRepo.getConversationCounters(organizationId, userId, connectionId);
    }

    async createMessage(conversationDbId: string, message: ChatMessage, organizationId?: string): Promise<void> {
        await this.messageRepo.createMessage(conversationDbId, message, organizationId);
        // Fallback: garante preview da conversa na UI mesmo se o RPC não atualizar o resumo
        await this.conversationRepo.updateLastMessage(
            conversationDbId,
            this.getSummaryFromMessage(message),
            message.timestamp,
        );
    }

    private getSummaryFromMessage(message: ChatMessage): string {
        const byType: Record<string, string> = {
            image: '📷 Imagem',
            video: '🎥 Vídeo',
            audio: '🎤 Áudio',
            ptt: '🎤 Áudio',
            document: '📄 Documento',
            sticker: '💟 Figurinha',
            location: '📍 Localização',
            contact: '👤 Contato',
            pix: '💳 PIX',
            poll: '📊 Enquete',
        };
        const forceType =
            message.type === 'pix' ||
            message.type === 'contact' ||
            message.type === 'location' ||
            message.type === 'poll';
        if (!forceType && message.text) return message.text;
        return byType[message.type] || message.type || 'Nova mensagem';
    }

    async getMessages(conversationDbId: string, organizationId?: string, limit = 80, offset = 0, signal?: AbortSignal): Promise<ChatMessage[]> {
        return this.messageRepo.getMessages(conversationDbId, organizationId, limit, offset, signal);
    }

    async getOlderMessages(
        conversationDbId: string,
        beforeTimestamp: Date,
        organizationId?: string,
        limit = 50,
    ): Promise<ChatMessage[]> {
        return this.messageRepo.getOlderMessages(conversationDbId, beforeTimestamp, organizationId, limit);
    }
}

export const chatRepository = new ChatRepository();
